import {useEffect, useState} from 'react';
import {isoDateToMomentNonUtc} from '../../forecast-app/shared/util/DateUtil';
import {AI_GATEWAY_URL} from './aiGateway';
import {subscribeAiCreditsExhausted} from './aiCreditsSignal';

/**
 * The account's AI credit balance for the current period, as the gateway reports it.
 * Only ever built from a response whose balance is known; anything else is represented as null.
 */
export type AiCreditUsage = {
	usedCredits: number;
	grantedCredits: number;
	remainingCredits: number;
	percentUsed: number;
	period: string;
	periodEnd: string;
	isTrial: boolean;
	enforced: boolean;
	/** Metered but never refused. Older gateways omit the field, which reads as false. */
	unlimited: boolean;
};

export const FOCUS_REFETCH_INTERVAL_MS = 5 * 60 * 1000;
export const EXHAUSTED_REFETCH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Whether the gateway is refusing AI requests for this account right now: a limited, enforced
 * allowance at or past 100%. An unknown balance is never exhausted.
 */
export const isAiCreditsExhausted = (usage: AiCreditUsage | null): boolean =>
	!!usage && !usage.unlimited && usage.enforced && usage.percentUsed >= 100;

const USAGE_URL = `${AI_GATEWAY_URL}/credits/usage`;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export const parseAiCreditUsage = (body: unknown): AiCreditUsage | null => {
	if (!body || typeof body !== 'object') return null;
	const data = body as Record<string, unknown>;
	if (data.known !== true) return null;

	const {used_credits, granted_credits, remaining_credits, percent_used, period, period_end, is_trial, enforced, unlimited} =
		data;
	if (
		!isFiniteNumber(used_credits) ||
		!isFiniteNumber(granted_credits) ||
		!isFiniteNumber(remaining_credits) ||
		!isFiniteNumber(percent_used)
	) {
		return null;
	}
	if (typeof period !== 'string' || typeof period_end !== 'string' || typeof enforced !== 'boolean') return null;

	return {
		usedCredits: used_credits,
		grantedCredits: granted_credits,
		remainingCredits: remaining_credits,
		percentUsed: percent_used,
		period,
		periodEnd: period_end,
		isTrial: is_trial === true,
		enforced,
		unlimited: unlimited === true,
	};
};

/**
 * The reset date as DD/MM/YYYY. period_end is a calendar date, so it is read in local time;
 * read as a UTC instant it would show the previous day anywhere west of UTC.
 */
export const formatCreditResetDate = (periodEnd: string): string | null => {
	if (!ISO_DATE.test(periodEnd)) return null;
	const date = isoDateToMomentNonUtc(periodEnd);
	return date && date.isValid() ? date.format('DD/MM/YYYY') : null;
};

type Subscriber = (usage: AiCreditUsage | null) => void;

// One store for the whole app, so the banners, the admin counter and the Nova controls share a
// single request. It fetches only while something subscribes, but the last balance it saw outlives
// the last unsubscribe: Nova's controls come and go with the page, and a page that mounts one must
// not show an exhausted account as having credits until the refetch lands. Sign-out loads a new
// page, which is what keeps one account's numbers from reaching the next.
const subscribers = new Set<Subscriber>();
let current: AiCreditUsage | null = null;
let lastFetchStartedAt: number | null = null;
let inFlight = false;
let refetchQueued = false;
let generation = 0;
let detach: (() => void) | null = null;
// Numbered so a reader can tell whether the balance on show was asked for before or after
// something it cares about happened (see getAiCreditRequestProgress).
let requestsStarted = 0;
let publishedFromRequest = 0;
// The last request that got any answer at all, usable or not. A response discarded because the
// store was reset in the meantime does not count.
let settledFromRequest = 0;
// The last request whose balance showed credits to spend. Zero until one has.
let availableFromRequest = 0;
const settledListeners = new Set<() => void>();
// While the allowance is spent nothing else would notice it coming back, so the store asks again
// on a timer until it does.
let exhaustedTimer: ReturnType<typeof setInterval> | null = null;

const syncExhaustedTimer = (): void => {
	const shouldPoll = detach !== null && isAiCreditsExhausted(current);
	if (shouldPoll && exhaustedTimer === null) {
		// eslint-disable-next-line @typescript-eslint/no-use-before-define -- load and publish call each other
		exhaustedTimer = setInterval(() => load(), EXHAUSTED_REFETCH_INTERVAL_MS);
	} else if (!shouldPoll && exhaustedTimer !== null) {
		clearInterval(exhaustedTimer);
		exhaustedTimer = null;
	}
};

const publish = (next: AiCreditUsage | null, fromRequest: number): void => {
	current = next;
	publishedFromRequest = fromRequest;
	if (next && !isAiCreditsExhausted(next)) availableFromRequest = fromRequest;
	syncExhaustedTimer();
	subscribers.forEach(subscriber => subscriber(next));
};

// Resolves to the new balance, to null when the gateway answered that the balance is unknown,
// or to undefined when there is no usable answer at all (network error, non-200, malformed body).
// Undefined keeps whatever is already shown: a transient failure must not hide a spent allowance.
const requestUsage = (): Promise<AiCreditUsage | null | undefined> => {
	if (typeof fetch !== 'function') return Promise.resolve(undefined);
	try {
		return fetch(USAGE_URL, {credentials: 'include', headers: {Accept: 'application/json'}})
			.then(response => (response.status === 200 ? response.json() : undefined))
			.then(body => {
				if (body && typeof body === 'object' && (body as Record<string, unknown>).known === false) return null;
				return parseAiCreditUsage(body) ?? undefined;
			})
			.catch(() => undefined);
	} catch {
		return Promise.resolve(undefined);
	}
};

const load = (): void => {
	if (inFlight) {
		// A response already on its way may predate whatever prompted this call.
		refetchQueued = true;
		return;
	}
	inFlight = true;
	lastFetchStartedAt = Date.now();
	requestsStarted += 1;
	const requestNumber = requestsStarted;
	const startedIn = generation;

	requestUsage().then(next => {
		if (startedIn !== generation) return;
		inFlight = false;
		settledFromRequest = requestNumber;
		if (next !== undefined) publish(next, requestNumber);
		settledListeners.forEach(listener => listener());
		if (refetchQueued) {
			refetchQueued = false;
			load();
		}
	});
};

const onFocus = (): void => {
	if (lastFetchStartedAt === null || Date.now() - lastFetchStartedAt >= FOCUS_REFETCH_INTERVAL_MS) {
		load();
	}
};

const attach = (): void => {
	window.addEventListener('focus', onFocus);
	const unsubscribeExhausted = subscribeAiCreditsExhausted(load);
	detach = () => {
		window.removeEventListener('focus', onFocus);
		unsubscribeExhausted();
	};
	// A balance kept from before may already be spent, and nothing else would restart the re-check.
	syncExhaustedTimer();
	load();
};

const reset = (): void => {
	if (detach) detach();
	detach = null;
	if (exhaustedTimer !== null) {
		clearInterval(exhaustedTimer);
		exhaustedTimer = null;
	}
	generation += 1;
	lastFetchStartedAt = null;
	inFlight = false;
	refetchQueued = false;
};

const subscribe = (subscriber: Subscriber): (() => void) => {
	subscribers.add(subscriber);
	if (subscribers.size === 1) attach();
	return () => {
		subscribers.delete(subscriber);
		if (subscribers.size === 0) reset();
	};
};

/** Drops the stored balance as well as the subscription state, as a fresh page would. For tests. */
export const resetAiCreditUsageForTests = (): void => {
	reset();
	current = null;
	publishedFromRequest = 0;
	settledFromRequest = 0;
	availableFromRequest = 0;
	requestsStarted = 0;
};

/** The balance last seen, read outside React. Null when unknown or never fetched on this page. */
export const getAiCreditUsage = (): AiCreditUsage | null => current;

/**
 * How many requests this store has started, which one the balance on show came from, which one
 * last got any answer, and which one last showed credits to spend. A caller that learned something
 * at request N can wait for `published > N` to know the balance on show was asked for afterwards,
 * for `settled > N` to know a request asked for afterwards has finished, even if it brought no
 * usable balance, or check `available > N` to know credits were seen to come back since.
 */
export const getAiCreditRequestProgress = (): {started: number; published: number; settled: number; available: number} => ({
	started: requestsStarted,
	published: publishedFromRequest,
	settled: settledFromRequest,
	available: availableFromRequest,
});

/**
 * Calls the listener each time a request settles, after any balance it brought is published.
 * Unlike useAiCreditUsage it fires even when the request failed or the balance did not change.
 */
export const subscribeAiCreditRequestSettled = (listener: () => void): (() => void) => {
	settledListeners.add(listener);
	return () => {
		settledListeners.delete(listener);
	};
};

/**
 * The account's AI credit balance, or null when it is unknown, still loading, or could not be
 * fetched. Fetches after the first render, never during it, so a page never waits on the gateway.
 * Refetches on window focus at most once per FOCUS_REFETCH_INTERVAL_MS, and at once whenever the
 * chat is refused for a spent allowance.
 */
export const useAiCreditUsage = (enabled = true): AiCreditUsage | null => {
	const [usage, setUsage] = useState<AiCreditUsage | null>(current);

	useEffect(() => {
		if (!enabled) return;
		setUsage(current);
		return subscribe(setUsage);
	}, [enabled]);

	return enabled ? usage : null;
};
